'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { Send, CheckCircle } from 'lucide-react';

const contactSchema = z.object({
  name: z.string().min(2, 'name'),
  email: z.string().email('email'),
  phone: z.string().optional(),
  company: z.string().optional(),
  message: z.string().min(10, 'message'),
});

type ContactFormData = z.infer<typeof contactSchema>;

const inputBase =
  'w-full rounded-lg border bg-surface px-4 py-2.5 text-body transition-colors focus:border-ink focus:outline-none';

export function ContactForm() {
  const t = useTranslations('contacts.form');
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    // TODO: integrate with Server Action or API endpoint
    console.log('Form data:', data);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="py-12 text-center">
        <CheckCircle className="mx-auto mb-4 h-12 w-12 text-ink" />
        <p className="text-subheading font-medium text-ink">{t('success')}</p>
      </div>
    );
  }

  /** Errors are never carried by color alone — border, text, and aria together. */
  const fieldError = (key: 'name' | 'email' | 'message') =>
    errors[key] ? t(`errors.${key}`) : undefined;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-2 block text-body-sm font-medium text-ink">
            {t('name')} *
          </label>
          <input
            {...register('name')}
            id="name"
            placeholder={t('namePlaceholder')}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'name-error' : undefined}
            className={`${inputBase} ${errors.name ? 'border-signal' : 'border-line-control'}`}
          />
          {fieldError('name') && (
            <p id="name-error" className="mt-2 text-body-sm text-signal-text">
              {fieldError('name')}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="mb-2 block text-body-sm font-medium text-ink">
            {t('email')} *
          </label>
          <input
            {...register('email')}
            id="email"
            type="email"
            placeholder={t('emailPlaceholder')}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'email-error' : undefined}
            className={`${inputBase} ${errors.email ? 'border-signal' : 'border-line-control'}`}
          />
          {fieldError('email') && (
            <p id="email-error" className="mt-2 text-body-sm text-signal-text">
              {fieldError('email')}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="mb-2 block text-body-sm font-medium text-ink">
            {t('phone')}
          </label>
          <input
            {...register('phone')}
            id="phone"
            type="tel"
            placeholder={t('phonePlaceholder')}
            className={`${inputBase} border-line`}
          />
        </div>

        <div>
          <label htmlFor="company" className="mb-2 block text-body-sm font-medium text-ink">
            {t('company')}
          </label>
          <input
            {...register('company')}
            id="company"
            placeholder={t('companyPlaceholder')}
            className={`${inputBase} border-line`}
          />
        </div>
      </div>

      <div>
        <label htmlFor="message" className="mb-2 block text-body-sm font-medium text-ink">
          {t('message')} *
        </label>
        <textarea
          {...register('message')}
          id="message"
          rows={5}
          placeholder={t('messagePlaceholder')}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? 'message-error' : undefined}
          className={`${inputBase} resize-none ${errors.message ? 'border-signal' : 'border-line-control'}`}
        />
        {fieldError('message') && (
          <p id="message-error" className="mt-2 text-body-sm text-signal-text">
            {fieldError('message')}
          </p>
        )}
      </div>

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto">
        {isSubmitting ? '…' : t('submit')}
        <Send className="h-4 w-4" />
      </Button>
    </form>
  );
}
